/**
 * Product Variant Handlers
 * Pure functions that return new state - caller does setState
 */

export const normalizeVariantOption = (option) => {
  if (option == null || option === "") return null;
  if (typeof option !== "object") {
    return { label: String(option), value: String(option) };
  }
  const value = option.value ?? option.label;
  if (value == null || value === "") return null;
  return { ...option, label: String(option.label ?? value), value: String(value) };
};

export const normalizeVariantAttributes = (attributes) => {
  let parsed = attributes;
  if (typeof parsed === "string") {
    try { parsed = JSON.parse(parsed); } catch (_) { parsed = {}; }
  }
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) parsed = {};
  return {
    ...parsed,
    size: normalizeVariantOption(parsed.size),
    color: normalizeVariantOption(parsed.color),
    material: normalizeVariantOption(parsed.material),
  };
};

export const normalizeVariant = (variant) => {
  const attributes = normalizeVariantAttributes(variant.attributes);
  const options = {};
  ["size", "color", "material"].forEach((field) => {
    options[field] = normalizeVariantOption(variant[field]) || attributes[field] ||
      { label: "", value: `no-${field}` };
  });
  return { ...variant, ...options, attributes: { ...attributes, ...options } };
};

export const hasInventoryValue = (value) => value != null && String(value).trim() !== "";

export const normalizeInventoryValue = (value) => {
  if (!hasInventoryValue(value)) return "";
  const clean = String(value).replace(/,/g, "").trim();
  return Number.isFinite(Number(clean)) ? String(Number(clean)) : clean;
};

// Store scalar values separately from the editable variant objects.
export const snapshotVariantInventory = (variants) => Object.freeze(variants.map((variant) =>
  Object.freeze({
    id: variant.id,
    quantity: normalizeInventoryValue(variant.quantity),
    cost_price: normalizeInventoryValue(variant.cost_price),
  }),
));

export const getVariantInventoryPayload = (variant, snapshot = []) => {
  const quantity = normalizeInventoryValue(variant.quantity);
  const costPrice = normalizeInventoryValue(variant.cost_price);
  if (!Number.isInteger(variant.id)) return { quantity, cost_price: costPrice };
  const original = snapshot.find((entry) => entry.id === variant.id);
  // An existing variant without a baseline must never write a guessed stock value.
  if (!original) return {};
  const payload = {};
  if (quantity !== "" && quantity !== original.quantity) {
    payload.quantity = quantity;
    payload.expected_quantity = original.quantity;
  }
  if (costPrice !== "" && costPrice !== original.cost_price) payload.cost_price = costPrice;
  return payload;
};

export const allocateVariantSkus = (variants, baseSku) => {
  const used = new Set(variants.filter((v) => v.sku).map((v) => String(v.sku).trim()));
  let suffix = 1;
  return variants.map((variant) => {
    if (variant.sku) return variant;
    while (used.has(`${baseSku}.${suffix}`)) suffix++;
    const sku = `${baseSku}.${suffix++}`;
    used.add(sku);
    return { ...variant, sku };
  });
};

/**
 * Extract unique options from all product attributes
 * @param {Array} allAttributes - Array of attribute objects from variants
 * @param {string} attributeName - "size", "color", or "material"
 * @returns {Array} Unique option objects [{label, value}]
 */
export const extractAllUniqueOptions = (allAttributes, attributeName) => {
  const allOptions = allAttributes
    .map((attr) => normalizeVariantAttributes(attr)[attributeName])
    .filter((opt) => opt && opt.value && !opt.value.startsWith("no-"));

  const uniqueOptionsMap = new Map();
  allOptions.forEach((option) => {
    if (option.value && !uniqueOptionsMap.has(option.value)) {
      uniqueOptionsMap.set(option.value, {
        label: option.label,
        value: option.value,
      });
    }
  });

  return Array.from(uniqueOptionsMap.values());
};

/**
 * Generate or merge variants from selected size/color/material combinations
 * @param {Object} params
 * @param {Array} params.existingVariants - Current generatedVariants
 * @param {string} params.price - Default price for new variants
 * @param {Array} params.size - Selected sizes [{label, value}]
 * @param {Array} params.color - Selected colors
 * @param {Array} params.material - Selected materials
 * @returns {Array|null} New merged variants array, or null if validation failed
 */
export const generateVariants = ({
  existingVariants,
  price,
  priceWholesale,
  costPrice,
  size,
  color,
  material,
}) => {
  if (!size || size.length === 0) {
    return null; // Caller should show validation error
  }

  // Build lookup map from existing variants
  const existingMap = new Map();
  for (const original of existingVariants) {
    const variant = normalizeVariant(original);
    const sizeValue = variant.size?.value || "no-size";
    const colorValue = variant.color?.value || "no-color";
    const materialValue = variant.material?.value || "no-material";
    existingMap.set(JSON.stringify([sizeValue, colorValue, materialValue]), variant);
  }

  const effectiveColors =
    color && color.length > 0 ? color : [{ label: "", value: "no-color" }];
  const effectiveMaterials =
    material && material.length > 0 ? material : [{ label: "", value: "no-material" }];

  // Generate and merge
  return size.map(normalizeVariantOption).filter(Boolean).flatMap((s) =>
    effectiveColors.map(normalizeVariantOption).filter(Boolean).flatMap((c) =>
      effectiveMaterials.map(normalizeVariantOption).filter(Boolean).map((m) => {
        const key = JSON.stringify([s.value, c.value, m.value]);
        if (existingMap.has(key)) {
          const existing = existingMap.get(key);
          return {
            ...existing, size: s, color: c, material: m,
            attributes: { ...existing.attributes, size: s, color: c, material: m },
          };
        }
        return {
          id: key,
          size: s,
          color: c,
          material: m,
          price: hasInventoryValue(price) ? price : "0",
          price_wholesale: priceWholesale ?? "",
          promo_price: "0",
          quantity: "1",
          cost_price: costPrice ?? "",
          exchange_value: "1",
          date_start: null,
          date_end: null,
          isOpen: false,
        };
      }),
    ),
  );
};

/**
 * Update a single field on a specific variant
 * @param {Array} variants - Current variants array
 * @param {string} variantId - ID of variant to update
 * @param {string} fieldName - Field to update
 * @param {*} value - New value
 * @returns {Array} Updated variants array
 */
export const updateVariantField = (variants, variantId, fieldName, value) => {
  return variants.map((variant) => {
    if (variant.id === variantId) {
      return { ...variant, [fieldName]: value };
    }
    return variant;
  });
};

/**
 * Apply master settings to all variants (only non-empty fields)
 * @param {Array} variants - Current variants
 * @param {Object} masterSettings - {price, promo_price, date_start, date_end}
 * @returns {Array} Updated variants
 */
export const applyMasterToAll = (variants, masterSettings) => {
  return variants.map((variant) => {
    const updated = { ...variant };
    if (hasInventoryValue(masterSettings.price)) updated.price = masterSettings.price;
    if (hasInventoryValue(masterSettings.price_wholesale)) {
      updated.price_wholesale = masterSettings.price_wholesale;
    }
    if (hasInventoryValue(masterSettings.promo_price)) updated.promo_price = masterSettings.promo_price;
    if (hasInventoryValue(masterSettings.quantity)) updated.quantity = masterSettings.quantity;
    if (hasInventoryValue(masterSettings.cost_price)) updated.cost_price = masterSettings.cost_price;
    if (hasInventoryValue(masterSettings.exchange_value)) {
      updated.exchange_value = masterSettings.exchange_value;
    }
    if (masterSettings.date_start) updated.date_start = masterSettings.date_start;
    if (masterSettings.date_end) updated.date_end = masterSettings.date_end;
    return updated;
  });
};

/**
 * Delete a variant by ID
 * @param {Array} variants - Current variants
 * @param {string} variantId - ID to remove
 * @returns {Array} Filtered variants
 */
export const removeVariant = (variants, variantId) => {
  return variants.filter((variant) => variant.id !== variantId);
};
